<template>
  <TransitionRoot as="template" :show="visible">
    <Dialog as="div" class="relative z-50" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-auto rounded-lg bg-white px-6 pt-6 pb-6 text-left shadow-xl transition-all sm:my-8 sm:w-11/12 sm:max-w-6xl sm:p-8 max-h-[calc(100vh-100px)]"
            >
              <div
                class="my-3 text-center sm:mt-0 sm:text-left flex flex-wrap gap-4 items-center justify-between border-b border-gray-200 pb-4"
              >
                <div>
                  <DialogTitle
                    as="h3"
                    class="text-lg font-semibold leading-6 text-gray-900"
                    >{{
                      props.filename || "PDF Förhandsgranskning"
                    }}</DialogTitle
                  >
                  <p class="text-sm text-gray-500 mt-1">
                    Använd piltangenterna för att navigera, klicka för att zooma
                    in, högerklicka för att zooma ut, ESC för att stänga
                  </p>
                  <div
                    v-if="props.isConverted"
                    class="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md"
                  >
                    <p class="text-sm text-yellow-800">
                      <i class="fas fa-exclamation-triangle mr-1"></i>
                      <strong>Konverterad förhandsgranskning:</strong> Detta är
                      en konverterad version som kanske inte är 100% sann mot
                      originalet. För att se originalfilen, använd
                      nedladdningsknappen i fillistan.
                    </p>
                  </div>
                </div>
                <i
                  @click="$emit('close')"
                  class="text-2xl font-bold fas fa-close cursor-pointer hover:text-gray-600 transition-colors"
                >
                </i>
              </div>

              <!-- Loading Indicator -->
              <div
                v-if="isLoading"
                class="w-full flex justify-center items-center py-20"
              >
                <div class="flex flex-col items-center gap-4">
                  <div
                    class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
                  ></div>
                  <p class="text-gray-600">Laddar PDF...</p>
                </div>
              </div>

              <!-- PDF Viewer with Side Navigation -->
              <div
                v-if="!isLoading && numPages > 0"
                class="w-full flex items-center justify-center bg-gray-100 p-4 rounded-lg gap-4"
              >
                <!-- Previous Page Button -->
                <button
                  @click="previousPage"
                  :disabled="currentPage <= 1"
                  class="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg"
                  title="Föregående sida"
                >
                  <i class="fas fa-chevron-left text-lg"></i>
                </button>
                <!-- PDF Canvas -->
                <div class="bg-white shadow-lg rounded-lg overflow-hidden">
                  <canvas
                    ref="canvas"
                    class="max-w-full h-auto border border-gray-200 cursor-pointer"
                    @click="handleCanvasClick"
                    @contextmenu="handleCanvasRightClick"
                  />
                </div>

                <!-- Next Page Button -->
                <button
                  @click="nextPage"
                  :disabled="currentPage >= numPages"
                  class="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg"
                  title="Nästa sida"
                >
                  <i class="fas fa-chevron-right text-lg"></i>
                </button>
              </div>
              <!-- Bottom Controls -->
              <div
                v-if="!isLoading && numPages > 0"
                class="mt-4 flex flex-col items-center gap-4"
              >
                <!-- Page Counter -->
                <div class="flex items-center">
                  <span class="text-sm font-medium text-gray-700">
                    Sida {{ currentPage }} av {{ numPages }}
                  </span>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, defineEmits, nextTick } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";

import * as PDFJS from "../../../../../public/scripts/pdf.min.js";
PDFJS.GlobalWorkerOptions.workerSrc = "/scripts/pdf.worker.min.js";

const props = defineProps<{
  pdfUrl: string;
  visible: boolean;
  filename?: string;
  isConverted?: boolean;
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
const numPages = ref(0);
const currentPage = ref(0);
const scale = ref(1);
const isLoading = ref(false);
let pdfDocument: any = null;

const emit = defineEmits(["close"]);

const loadPdf = async () => {
  try {
    isLoading.value = true;
    const pdf = await PDFJS.getDocument(props.pdfUrl).promise;
    pdfDocument = pdf;
    numPages.value = pdf.numPages;
    if (numPages.value > 0) {
      currentPage.value = 1;
    }
  } catch (error) {
    console.error("Error loading PDF:", error);
  } finally {
    isLoading.value = false;
    // The watcher will handle rendering when loading is complete
  }
};

const renderPage = async () => {
  try {
    console.log("renderPage called:", {
      hasCanvas: !!canvas.value,
      hasPdfDocument: !!pdfDocument,
      currentPage: currentPage.value,
      numPages: numPages.value,
    });

    if (!canvas.value || !pdfDocument || currentPage.value <= 0) {
      console.log("renderPage early return");
      return;
    }

    const ctx = canvas.value.getContext("2d");
    if (!ctx) {
      console.log("No canvas context");
      return;
    }

    console.log("Getting page:", currentPage.value);
    const page = await pdfDocument.getPage(currentPage.value);

    const viewport = page.getViewport({ scale: scale.value });
    canvas.value.width = viewport.width;
    canvas.value.height = viewport.height;

    console.log("Rendering page with viewport:", {
      width: viewport.width,
      height: viewport.height,
      scale: scale.value,
    });
    await page.render({
      canvasContext: ctx,
      viewport,
    }).promise;

    console.log("Page rendered successfully");
  } catch (error) {
    console.error("Error rendering PDF page:", error);
  }
};

const previousPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    await renderPage();
  }
};

const nextPage = async () => {
  if (currentPage.value < numPages.value) {
    currentPage.value++;
    await renderPage();
  }
};

const zoomIn = async () => {
  if (scale.value < 3) {
    scale.value = Math.min(scale.value + 0.25, 3);
    await renderPage();
  }
};

const zoomOut = async () => {
  if (scale.value > 0.5) {
    scale.value = Math.max(scale.value - 0.25, 0.5);
    await renderPage();
  }
};

// Canvas click handlers for zoom
const handleCanvasClick = async (event: MouseEvent) => {
  event.preventDefault();
  await zoomIn();
};

const handleCanvasRightClick = async (event: MouseEvent) => {
  event.preventDefault();
  await zoomOut();
};

// Keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (!props.visible) return;

  switch (event.key) {
    case "ArrowLeft":
      event.preventDefault();
      previousPage();
      break;
    case "ArrowRight":
      event.preventDefault();
      nextPage();
      break;
    case "+":
    case "=":
      event.preventDefault();
      zoomIn();
      break;
    case "-":
      event.preventDefault();
      zoomOut();
      break;
    case "Escape":
      event.preventDefault();
      emit("close");
      break;
  }
};

// Load PDF when component mounts and when props change
onMounted(() => {
  // Add keyboard event listener
  document.addEventListener("keydown", handleKeydown);

  if (props.visible) {
    loadPdf();
  }
});

onUnmounted(() => {
  // Remove keyboard event listener
  document.removeEventListener("keydown", handleKeydown);
});

// Watch for changes in visibility and pdfUrl
watch(
  () => [props.visible, props.pdfUrl],
  ([newVisible, newPdfUrl], [oldVisible, oldPdfUrl]) => {
    if (
      newVisible &&
      newPdfUrl &&
      (newVisible !== oldVisible || newPdfUrl !== oldPdfUrl)
    ) {
      loadPdf();
    }
  }
);

// Watch for when loading finishes and canvas becomes available
watch(
  () => [isLoading.value, canvas.value],
  async ([newIsLoading, newCanvas]) => {
    if (!newIsLoading && newCanvas && pdfDocument && currentPage.value > 0) {
      console.log("Canvas available after loading, rendering page");
      await nextTick();
      await renderPage();
    }
  }
);
</script>
